 
'use strict';
import { Box, Stack, Typography, useTheme } from '@mui/joy';
import { max } from 'd3-array';
import { SHORT_DOLLAR_FORMATTER } from '../../lib/format';
import React from 'react';
import ChartTooltip from './ChartTooltip';
import UiTooltip from '../Controls/UiTooltip';
//import { toTitleCase } from '../../lib/utils';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface IProps {
	office: string;
	topic: string;
	maxValue: number;
	chartData: {
		group: string;
		partyLabel: string;
		chamber: string;
		label: string;
		value: number;
		numCand: number;
	}[];
}
const SimpleBarChart = ({ office, topic, maxValue, chartData }: IProps) => {
	const theme = useTheme();
	const COLORS: { [key: string]: string } = {
		MenDEM: theme.colorSchemes.light.palette.categoryPartyDemocratMen.solidBg,
		MenGOP: theme.colorSchemes.light.palette.categoryPartyRepublicanMen.solidBg,
		WomenDEM:
			theme.colorSchemes.light.palette.categoryPartyDemocratWomen.solidBg,
		WomenGOP:
			theme.colorSchemes.light.palette.categoryPartyRepublicanWomen.solidBg
	};
	const title = office;
	//scale % width of bar to 200K
	const SCALES = [
		100000, 200000, 400000, 600000, 800000, 1000000, 2000000, 4000000, 6000000,
		8000000, 12000000, 16000000, 20000000, 24000000, 28000000, 32000000,
		36000000, 40000000, 60000000, 80000000
	];
	const TICKS = [
		[0, 25000, 50000, 75000, 100000],
		[0, 50000, 100000, 150000, 200000],
		[0, 100000, 200000, 300000, 400000],
		[0, 150000, 300000, 450000, 600000],
		[0, 200000, 400000, 60000, 800000],
		[0, 250000, 500000, 750000, 1000000],
		[0, 500000, 1000000, 1500000, 2000000],
		[0, 1000000, 2000000, 3000000, 4000000],
		[0, 1500000, 3000000, 4500000, 6000000],
		[0, 2000000, 4000000, 6000000, 8000000],
		[0, 3000000, 6000000, 9000000, 12000000],
		[0, 4000000, 8000000, 12000000, 16000000],
		[0, 5000000, 10000000, 15000000, 20000000],
		[0, 6000000, 12000000, 18000000, 24000000],
		[0, 7000000, 14000000, 21000000, 28000000],
		[0, 8000000, 16000000, 24000000, 32000000],
		[0, 9000000, 18000000, 27000000, 36000000],
		[0, 10000000, 20000000, 30000000, 40000000],
		[0, 15000000, 30000000, 45000000, 60000000],
		[0, 20000000, 40000000, 60000000, 80000000]
	];
	const tickFormat: { [x: number]: string } = {
		12000000: '$12M',
		16000000: '$16M',
		20000000: '$20M',
		24000000: '$24M',
		28000000: '$28M',
		32000000: '$32M',
		36000000: '$36M',
		40000000: '$40M',
		60000000: '$60M',
		80000000: '$80M'
	};

	const rep: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel }) => partyLabel === 'GOP'
	);
	const dem: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel }) => partyLabel === 'DEM'
	);
	// const ind: { [x: string]: string | number }[] = chartData?.filter(
	// 	({ partyLabel }) => partyLabel === 'IND'
	// );

	const index = max(
		SCALES.map((_, i) => (maxValue > SCALES[i] ? i + 1 : 0))
	) as number;
	const scale = SCALES[index];
	const isFiltered = false;
	//	const isFiltered = raceFilter[0] === 'true' && election === 'state';
	//	const spacer = isFiltered ? 24 : 64;
	//console.log('maxVal', maxValue);
	//console.log('chart', chartData);
	const matches = useMediaQuery(theme.breakpoints.up('sm'));
	//const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
	return (
		<figure
			style={{
				marginBlock: 'unset',
				marginInline: 'unset',
				backgroundColor: theme.palette.chartElementsBackground.bg
			}}
			aria-label='a bar chart showing the average total raised for each party and gender.'
		>
			<Stack
				direction='column'
				sx={{
					margin: '1rem 1rem 1rem 0rem'
				}}
			>
				<Typography
					level='chartSubtitle'
					sx={{
						marginLeft: '3rem',
						marginTop: '.5rem'
					}}
				>
					{title}
				</Typography>

				<Box
					sx={{
						margin: {
							xs: '1rem 0rem 1.25rem 3rem',
							sm: '1rem 3rem 1.25rem 3rem'
						},
						position: 'relative'
					}}
				>
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							width: '100%',
							height: '100%'
						}}
					>
						{TICKS[index].map((tick, i) => (
							<React.Fragment key={tick * Math.random()}>
								<Box
									key={tick * Math.random()}
									sx={{
										borderRight:
											i === 0 ? '1px solid black' : '1px solid lightGray',
										height: '100%',
										position: 'absolute',
										top: 0,
										left: `${(tick / scale) * 100}%}`
									}}
								/>
								<Box
									sx={{
										position: 'absolute',
										bottom: '-20px',
										fontSize: '14px'
									}}
									style={
										i === 4
											? { left: `calc(${(tick / scale) * 100}% - 15px)` }
											: i === 0
											? { left: `calc(${(tick / scale) * 100}% - 10px)` }
											: { display: 'none' }
									}
								>
									{tick > 10000000
										? tickFormat[tick]
										: SHORT_DOLLAR_FORMATTER(tick)}
								</Box>
							</React.Fragment>
						))}
					</Box>
					{/* dem box */}

					<Box
						sx={{
							my: '2rem',
							position: 'relative',
							//so that when regular bar chart is next to disagg list, each section is never higher than 2
							// height:
							// 	election === 'congress' || page === 'national'
							// 		? `${dem.length * spacer}px`
							// 		: `${maxInParty.dems * spacer}px`,
							transition: 'all 1s ease'
						}}
					>
						<Box
							sx={{
								position: 'absolute',
								//top: '2rem',
								width: '100%',
								height: '100%'
							}}
						>
							<Box
								sx={{
									position: 'absolute',
									left: {
										xs: '-3rem',
										sm: '-2.5rem'
									},
									top: 'calc(50% - 20px)',
									fontFamily: 'Source Sans Pro',
									fontWeight: 700,
									fontSize: '25px',
									color:
										theme.colorSchemes.light.palette.categoryPartyDemocratWomen
											.solidBg,
									transform: 'rotate(-90deg)'
								}}
							>
								{dem?.length ? 'DEM' : ''}
							</Box>

							{/* //transparent label bars that sit behind the colored bars*/}
							{dem?.map((d) => {
								return (
									<Box
										key={d.label + 'dem'}
										sx={{
											width: 'calc(100% - 1px)',
											height: '60px',
											backgroundColor: 'tranparent',
											marginBottom: '.25rem',
											marginLeft: '1px',
											transition: 'all 1s ease',
											position: 'relative'
										}}
									>
										<Typography
											level='label'
											sx={{
												color: d.value ? `${COLORS[d.group]}` : 'white',
												position: 'absolute',
												top: '1.25rem',
												left: '1rem',
												fontSize: '20px',
												whiteSpace: 'nowrap',
												transition: 'all 1s ease'
											}}
										>
											{d.label}
										</Typography>
									</Box>
								);
							})}
						</Box>
						{/* colored bar with white labels */}
						{dem?.map((d, i) => {
							return (
								<UiTooltip
									key={d.label + 'dem' + `${i}`}
									title={
										<ChartTooltip
											titleText={d.chamber as string}
											d={d as { [x: string]: string | number }}
											topic={topic}
											isFiltered={isFiltered}
										/>
									}
									variant='plain'
									sx={{ borderRadius: '0px' }}
									arrow
									placement={!matches ? 'top' : 'left'}
								>
									<Box
										sx={{
											width: `${((d.value as number) / scale) * 100}%`,
											maxWidth: '100%',
											height: '60px',
											backgroundColor: COLORS[d.group],
											marginBottom: '.25rem',
											transition: 'all 1s ease',
											zIndex: 3,
											position: 'relative',
											overflow: 'hidden'
										}}
									>
										<Typography
											level='label'
											sx={{
												color: 'white',
												position: 'absolute',
												top: '1.25rem',

												left: '1rem',
												fontSize: '20px',
												whiteSpace: 'nowrap'
											}}
										>
											{d.label}
										</Typography>
									</Box>
								</UiTooltip>
							);
						})}
					</Box>

					<Box
						sx={{
							my: '2rem',
							// height:
							// 	election === 'congress' || page === 'national'
							// 		? `${rep.length * spacer}px`
							// 		: `${maxInParty.reps * spacer}px`,
							transition: 'all 1s ease',
							position: 'relative'
						}}
					>
						{/*  */}
						{/* gop box*/}
						<Box
							sx={{
								position: 'absolute',
								width: '100%'
							}}
						>
							<Box
								sx={{
									position: 'absolute',
									left: {
										xs: '-3rem',
										sm: '-2.5rem'
									},
									top: 'calc(50% - 18px)',
									fontFamily: 'Source Sans Pro',
									fontWeight: 700,
									fontSize: '25px',
									color:
										theme.colorSchemes.light.palette
											.categoryPartyRepublicanWomen.solidBg,
									transform: 'rotate(-90deg)'
								}}
							>
								{'GOP'}
							</Box>
							{/* transparent bars with labels*/}
							{rep?.map((d) => {
								return (
									<Box
										key={d.label + 'gop'}
										sx={{
											width: 'calc(100% - 1px)',
											height: '60px',
											backgroundColor: 'tranparent',
											marginBottom: '.25rem',
											marginLeft: '1px',
											transition: 'all 1s ease',
											position: 'relative'
										}}
									>
										<Typography
											level='label'
											sx={{
												color: d.value ? `${COLORS[d.group]}` : 'white',
												position: 'absolute',
												top: '1.25rem',
												left: '1rem',
												fontSize: '20px',
												whiteSpace: 'nowrap'
											}}
										>
											{d.label}
										</Typography>
									</Box>
								);
							})}
						</Box>

						{/* colored bar with white labels */}
						{rep?.map((d, i) => {
							return (
								<UiTooltip
									key={d.label + 'gop' + `${i}`}
									title={
										<ChartTooltip
											titleText={d.chamber as string}
											d={d as { [x: string]: string | number }}
											topic={topic}
											isFiltered={isFiltered}
										/>
									}
									variant='plain'
									sx={{ borderRadius: '0px' }}
									arrow
									placement={!matches ? 'top' : 'left'}
								>
									<Box
										sx={{
											width: `${((d.value as number) / scale) * 100}%`,
											maxWidth: '100%',
											height: '60px',
											backgroundColor: COLORS[d.group],
											marginBottom: '.25rem',
											transition: 'all 500ms ease',
											zIndex: 3,
											position: 'relative',
											overflow: 'hidden'
										}}
									>
										<Typography
											level='label'
											sx={{
												color: 'white',
												position: 'absolute',
												top: '1.25rem',

												left: '1rem',
												fontSize: '20px',
												whiteSpace: 'nowrap'
											}}
										>
											{d.label}
										</Typography>
									</Box>
								</UiTooltip>
							);
						})}
					</Box>
				</Box>
			</Stack>
		</figure>
	);
};

export default SimpleBarChart;
